import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { LayoutDashboard, Package, Users, FileText, Settings, LogOut, Plus, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu } from 'lucide-react';

const LOGO_DARK = 'https://customer-assets.emergentagent.com/job_budget-gen-3/artifacts/pua56wyb_logo%20cauratech%20fondo%20oscuro.png';
const ISOLOGO_DARK = 'https://customer-assets.emergentagent.com/job_budget-gen-3/artifacts/hs5nxkvk_isologo%20cauratech%20fondo%20oscuro.png';

const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
  { to: '/quotes', label: 'Presupuestos', icon: FileText, testid: 'nav-quotes' },
  { to: '/catalog', label: 'Catálogo', icon: Package, testid: 'nav-catalog' },
  { to: '/clients', label: 'Clientes', icon: Users, testid: 'nav-clients' },
  { to: '/settings', label: 'Configuración', icon: Settings, testid: 'nav-settings' },
];

function SidebarContent({ onNavigate }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-5 py-5">
        <img src={ISOLOGO_DARK} alt="Cuaratech" className="h-9 w-9 rounded-md" />
        <div>
          <div className="font-display text-base font-semibold leading-none">Cuaratech</div>
          <div className="mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">Cotizador</div>
        </div>
      </div>
      <div className="divider-accent mx-4" />
      <nav className="flex-1 space-y-1 px-3 py-4">
        {NAV.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                isActive
                  ? 'bg-[var(--surface-3)] text-foreground shadow-[inset_2px_0_0_0_hsl(var(--primary))]'
                  : 'text-muted-foreground hover:bg-[var(--surface-2)] hover:text-foreground'
              }`
            }
          >
            <Icon className="h-4 w-4" />
            <span className="font-medium">{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-4 pb-4">
        <div className="glass rounded-xl p-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2 text-foreground">
            <ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))]" />
            <span className="font-medium">Verificación QR</span>
          </div>
          <p className="mt-1 leading-relaxed">Cada presupuesto incluye un QR de autenticidad escaneable.</p>
        </div>
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="relative z-10 flex min-h-screen">
      {/* Desktop sidebar */}
      <aside className="hidden w-[260px] shrink-0 border-r border-[var(--glass-border)] bg-[#0A0E1A]/60 backdrop-blur-xl lg:block">
        <SidebarContent />
      </aside>

      <main className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-20 border-b border-[var(--glass-border)] bg-[#0A0E1A]/70 backdrop-blur-xl">
          <div className="flex h-16 items-center gap-3 px-4 sm:px-6 lg:px-8">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] border-[var(--glass-border)] bg-[#0A0E1A] p-0">
                <SidebarContent />
              </SheetContent>
            </Sheet>

            <div className="flex items-center gap-2 lg:hidden">
              <img src={ISOLOGO_DARK} alt="Cuaratech" className="h-7 w-7 rounded" />
              <span className="font-display text-sm font-semibold">Cuaratech</span>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Button
                data-testid="topbar-new-quote-button"
                onClick={() => navigate('/quotes/new')}
                className="btn-primary h-10"
              >
                <Plus className="mr-1.5 h-4 w-4" /> Nuevo presupuesto
              </Button>
              <div className="hidden flex-col items-end sm:flex">
                <span className="text-sm font-medium leading-none">{user?.name}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                data-testid="topbar-logout-button"
                title="Cerrar sesión"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        <div className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
          {children}
        </div>
      </main>
    </div>
  );
}
